import { OpenAPI } from '../api-client';
import config from '../config/appConfig';

const RETURN_URL_KEY = 'authReturnUrl';
const AUTH_STORAGE_KEY = 'authToken';

// Класс для управления состоянием аутентификации
class AuthService {
  private token: string | null = null;
  private onAuthChangeCallbacks: Array<() => void> = [];

  constructor() {
    // Слушаем события storage для синхронизации между вкладками
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  // Обработчик событий storage для синхронизации между вкладками
  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === AUTH_STORAGE_KEY) {
      // Токен изменился в другой вкладке
      const newToken = event.newValue;

      if (newToken) {
        // Авторизация произошла в другой вкладке
        this.token = newToken;
        this.setAuthHeaders();
        this.notifyAuthChange();

        // Если мы на странице логина, редиректим на целевую страницу
        if (window.location.pathname === '/login') {
          // Пытаемся получить returnUrl из query-параметра текущего URL (приоритет)
          const urlParams = new URLSearchParams(window.location.search);
          const redirectParam = urlParams.get('redirect');

          // Приоритет: query-параметр > localStorage > /profile
          const returnUrl = redirectParam || this.getReturnUrl() || '/profile';

          if (returnUrl && returnUrl !== '/login') {
            this.clearReturnUrl();
            window.location.href = returnUrl;
          } else {
            this.clearReturnUrl();
            window.location.href = '/profile';
          }
        } else {
          // Если мы на другой странице - просто обновляем
          window.location.reload();
        }
      } else {
        // Разлогинивание в другой вкладке
        this.token = null;
        this.setAuthHeaders();
        this.notifyAuthChange();
      }
    }
  }

  // Подписка на изменения состояния аутентификации
  onAuthChange(callback: () => void): () => void {
    this.onAuthChangeCallbacks.push(callback);
    return () => {
      this.onAuthChangeCallbacks = this.onAuthChangeCallbacks.filter((cb) => cb !== callback);
    };
  }

  // Уведомление подписчиков об изменении состояния аутентификации
  private notifyAuthChange(): void {
    this.onAuthChangeCallbacks.forEach((callback) => callback());
  }

  // Сохраняем токен после успешной аутентификации
  setToken(token: string): void {
    this.token = token;
    // Сохраняем токен в localStorage для сохранения между сессиями
    localStorage.setItem(AUTH_STORAGE_KEY, token);

    // Устанавливаем заголовок авторизации
    this.setAuthHeaders();

    // Уведомляем подписчиков
    this.notifyAuthChange();
  }

  // Получаем текущий токен
  getToken(): string | null {
    if (!this.token) {
      // Проверяем наличие токена в localStorage
      this.token = localStorage.getItem(AUTH_STORAGE_KEY);
    }
    return this.token;
  }

  // Удаляем токен при выходе
  removeToken(): void {
    this.token = null;
    localStorage.removeItem(AUTH_STORAGE_KEY);

    // Удаляем заголовок авторизации
    this.setAuthHeaders();

    // Уведомляем подписчиков
    this.notifyAuthChange();
  }

  // Проверяем, авторизован ли пользователь
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Сохраняем URL для редиректа после авторизации
  setReturnUrl(url: string): void {
    localStorage.setItem(RETURN_URL_KEY, url);
  }

  // Получаем URL для редиректа после авторизации
  getReturnUrl(): string | null {
    return localStorage.getItem(RETURN_URL_KEY);
  }

  // Очищаем URL для редиректа
  clearReturnUrl(): void {
    localStorage.removeItem(RETURN_URL_KEY);
  }

  // Устанавливаем токен авторизации в глобальный конфиг OpenAPI
  setAuthHeaders(): void {
    // Устанавливаем базовый URL из конфигурации
    OpenAPI.BASE = config.apiUrl;

    if (this.isAuthenticated()) {
      OpenAPI.HEADERS = {
        ...(OpenAPI.HEADERS || {}),
        Authorization: `Bearer ${this.getToken()}`,
      } as any;
    } else {
      const headers = { ...(OpenAPI.HEADERS || {}) };
      delete (headers as any)['Authorization'];
      OpenAPI.HEADERS = headers as any;
    }
  }
}

// Экспортируем глобальный экземпляр authService
export const authService = new AuthService();

// Экспортируем класс для возможности создания других экземпляров при необходимости
export default AuthService;
